/* Zaroori Jini OMS — Customer Help Page: Connect Your Amazon Seller Account.
 * Public route /help/amazon-connection — linkable from the Amazon connector.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, KeyRound, Zap, RefreshCw, Package, ClipboardList, AlertTriangle,
  HelpCircle, Lock, LogOut, ChevronDown, ChevronRight, CheckCircle2,
} from "lucide-react";
import {
  MarketingShell, Section, SectionTitle, MUTED, INK, RED, BORDER, CREAM,
} from "@/components/marketing/atoms";
import { LegalHero } from "@/pages/Privacy";

/* ------------------------- Reusable step card ------------------------- */
const StepCard = ({ n, title, children, testid }) => (
  <div data-testid={testid} style={{
    display: "grid", gridTemplateColumns: "40px 1fr", gap: 18,
    padding: "20px 22px", background: "#fff", border: `1px solid ${BORDER}`,
    borderRadius: 6, marginBottom: 14,
  }}>
    <div style={{
      width: 34, height: 34, borderRadius: "50%", background: RED, color: "#fff",
      display: "grid", placeItems: "center", fontWeight: 700, fontSize: 14,
    }}>{n}</div>
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, color: INK, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.65 }}>{children}</div>
    </div>
  </div>
);

/* ------------------------- FAQ accordion ------------------------- */
const FAQ = ({ q, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid} style={{
      borderBottom: `1px solid ${BORDER}`, padding: "14px 0",
    }}>
      <button onClick={() => setOpen(!open)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 12,
          background: "transparent", border: 0, cursor: "pointer",
          fontSize: 14.5, fontWeight: 600, color: INK, textAlign: "left",
        }}>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        {q}
      </button>
      {open && (
        <div style={{ padding: "10px 0 0 28px", color: MUTED, fontSize: 14, lineHeight: 1.65 }}>
          {children}
        </div>
      )}
    </div>
  );
};

/* ------------------------- Placeholder screenshot ------------------------- */
const ScreenshotPlaceholder = ({ label }) => (
  <div style={{
    marginTop: 10, marginBottom: 8, padding: "42px 20px",
    border: `2px dashed ${BORDER}`, borderRadius: 4, background: CREAM,
    textAlign: "center", color: MUTED, fontSize: 12,
  }}>
    <div style={{ fontWeight: 600, letterSpacing: 0.4, marginBottom: 4 }}>SCREENSHOT</div>
    <div>{label}</div>
  </div>
);

/* ============================================================= */
export default function AmazonConnectionHelp() {
  return (
    <MarketingShell testid="help-amazon-connection">
      <LegalHero eyebrow="Onboarding Guide"
                  title="Connect your Amazon Seller Account"
                  updated="Zaroori Jini OMS · self-service guide" />

      <Section style={{ background: "#fff", paddingTop: 40 }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>

          {/* ---- Intro ---- */}
          <p style={{ fontSize: 16, color: MUTED, lineHeight: 1.7 }}>
            This guide walks you through connecting your Amazon Seller Central
            account to Zaroori Jini in under 3 minutes. You <b>do not need</b> an
            Amazon developer account, an SP-API application, or any technical
            setup — Zaroori Jini owns the Amazon application and merchants
            simply authorize us like any Shopify or SaaS install.
          </p>

          {/* ---- Prerequisites ---- */}
          <SectionTitle eyebrow="Before you begin" title="Prerequisites" center={false} />
          <ul style={{ color: MUTED, fontSize: 14.5, lineHeight: 1.85, paddingLeft: 20 }}>
            <li>You are the <b>Primary user</b> of your Amazon Seller Central account (or a user with &quot;Manage Your Applications&quot; permission).</li>
            <li>Your seller account is active on at least one marketplace: Amazon.in, .com, .co.uk, or .de.</li>
            <li>You have a Zaroori Jini workspace and can reach <b>Marketplace Hub → Amazon</b>.</li>
            <li>Two-Step Verification on Amazon is enabled (Amazon requires this for OAuth).</li>
          </ul>

          {/* ---- How OAuth works ---- */}
          <SectionTitle eyebrow="How it works" title="OAuth in plain English" center={false} />
          <p style={{ color: MUTED, fontSize: 14.5, lineHeight: 1.75 }}>
            OAuth is Amazon&apos;s way of saying <i>&quot;yes, this app can access my
            store on my behalf&quot;</i>. You never share your Amazon password with
            Zaroori Jini. You&apos;ll click a button, sign in on Amazon&apos;s own site,
            approve a specific list of permissions, and Amazon hands us a
            long-lived <b>refresh token</b> — a token you can revoke any time
            from your Seller Central account.
          </p>

          {/* ---- Step by step ---- */}
          <SectionTitle eyebrow="Step by step" title="Connecting in 5 clicks" center={false} />

          <StepCard n={1} title="Open the Amazon connector" testid="help-step-1">
            In Zaroori Jini, go to <b>Marketplace Hub → Amazon</b>. On the
            Settings tab, keep the default <b>Connect Automatically</b> mode
            selected.
            <ScreenshotPlaceholder label="Marketplace Hub → Amazon → Settings, 'Connect Automatically' selected" />
          </StepCard>

          <StepCard n={2} title="Pick your Amazon marketplace region" testid="help-step-2">
            Choose the region your Seller Central account is registered in:
            India (amazon.in), USA (amazon.com), UK (amazon.co.uk), or Germany
            (amazon.de). If you sell in multiple regions, complete this flow
            once per region — Zaroori Jini treats each region as a separate
            connection.
          </StepCard>

          <StepCard n={3} title="Click 'Connect with Amazon'" testid="help-step-3">
            You&apos;ll be sent to Amazon Seller Central&apos;s consent page. If
            you&apos;re already signed in, you go straight to the permissions
            screen; otherwise Amazon will ask you to sign in first.
            <ScreenshotPlaceholder label="Amazon Seller Central 'Authorize Zaroori Jini' consent page" />
          </StepCard>

          <StepCard n={4} title="Approve the requested permissions" testid="help-step-4">
            Amazon shows the exact permissions Zaroori Jini is requesting.
            Review them and click <b>Confirm</b>. The full list is in the
            &quot;Required Seller Central permissions&quot; section below.
          </StepCard>

          <StepCard n={5} title="Land back on Zaroori Jini" testid="help-step-5">
            After Amazon confirms, we redirect you back to Zaroori Jini with a
            green &quot;Connected via OAuth&quot; banner. That&apos;s it — you can now
            start a historical import or enable inventory sync.
            <ScreenshotPlaceholder label="Zaroori Jini Amazon connector showing 'Connected via OAuth' banner + IAM/notifications chips" />
          </StepCard>

          {/* ---- Permissions ---- */}
          <SectionTitle eyebrow="Access requested"
                         title="Required Seller Central permissions" center={false} />
          <PermRow scope="Orders" desc="Read order data — status, items, addresses, buyer email token." />
          <PermRow scope="Inventory" desc="Read + write inventory quantities to keep Amazon stock in sync with your central inventory." />
          <PermRow scope="Listings" desc="Read + write product listings for onboarding and catalog updates." />
          <PermRow scope="Reports" desc="Read fulfillment, settlement, and finance reports for reconciliation." />
          <PermRow scope="Notifications" desc="Read + write subscriptions so Amazon can push order-change and settlement events to Zaroori Jini in real time." />

          {/* ---- Connection verification ---- */}
          <SectionTitle eyebrow="After connecting" title="Verify the connection" center={false} />
          <p style={{ color: MUTED, fontSize: 14.5, lineHeight: 1.75 }}>
            Zaroori Jini automatically runs diagnostics right after OAuth. In the
            connected panel you&apos;ll see three chips:
          </p>
          <ul style={{ color: MUTED, fontSize: 14.5, lineHeight: 1.85, paddingLeft: 20 }}>
            <li><b>Status · healthy</b> — Zaroori Jini reached your Seller Central account and got a valid refresh token.</li>
            <li><b>Notifications · SQS / polling</b> — Whether we&apos;re getting Amazon events in real time (SQS) or polling every 5 minutes.</li>
            <li><b>Region · IN / US / UK / DE</b> — Which marketplace this connection covers.</li>
          </ul>
          <p style={{ color: MUTED, fontSize: 14.5, lineHeight: 1.75 }}>
            If Status is anything other than <b>healthy</b>, open the connection
            → click <b>Run diagnostics</b>. You&apos;ll see a 6-step checklist
            (LWA, refresh token, IAM, marketplaceParticipations) — each with a
            clear reason for any failure.
          </p>

          {/* ---- Historical import ---- */}
          <SectionTitle eyebrow="Backfill" title="Import historical orders" center={false} />
          <p style={{ color: MUTED, fontSize: 14.5, lineHeight: 1.75 }}>
            Once connected, you can pull historical orders and inventory into
            Zaroori Jini. Go to <b>Marketplace Hub → Amazon → Import</b>, pick a
            date range (30 / 90 / 180 / 365 days or custom), and choose which
            entities to backfill: orders, inventory, listings, or settlements.
            The job runs in the background and you&apos;ll see live progress in the
            same panel — pause, resume, or cancel any time.
          </p>

          {/* ---- Inventory sync ---- */}
          <SectionTitle eyebrow="Live sync" title="Inventory & order synchronization"
                         center={false} />
          <ul style={{ color: MUTED, fontSize: 14.5, lineHeight: 1.85, paddingLeft: 20 }}>
            <li><b>Inventory sync</b> — Every stock movement in Zaroori Jini is pushed to Amazon within seconds. If you edit stock in Seller Central, we hear the &quot;inventory_levels/update&quot; equivalent event and reconcile.</li>
            <li><b>Order sync</b> — New Amazon orders appear in your Zaroori Jini Order Workspace in real time (SQS mode) or within the polling window (5 min default). Fulfillment status changes flow both ways.</li>
            <li><b>Settlement sync</b> — Settlement report notifications trigger an automatic import into your Finance module for reconciliation.</li>
          </ul>

          {/* ---- What we access ---- */}
          <SectionTitle eyebrow="Transparency" title="What Zaroori Jini can access"
                         center={false} />
          <p style={{ color: MUTED, fontSize: 14.5, lineHeight: 1.75 }}>
            We only access data required to power the OMS features you&apos;ve
            enabled. We <b>never</b>:
          </p>
          <ul style={{ color: MUTED, fontSize: 14.5, lineHeight: 1.85, paddingLeft: 20 }}>
            <li>Change your Amazon account settings, billing, or bank details.</li>
            <li>Access personally identifiable buyer information beyond what Amazon exposes to sellers.</li>
            <li>Share your seller data with other tenants or third parties.</li>
            <li>Store your Amazon password — we never see it. Only a revocable refresh token.</li>
          </ul>

          {/* ---- Security & privacy ---- */}
          <SectionTitle eyebrow="Security" title="Security & privacy"
                         center={false} />
          <SecurityRow icon={<Lock size={18} />} title="Encryption at rest"
            desc="Refresh tokens, access tokens, and LWA client secrets are AES-256-encrypted before touching our database." />
          <SecurityRow icon={<ShieldCheck size={18} />} title="Encryption in transit"
            desc="Every call between Zaroori Jini and Amazon uses TLS 1.2+." />
          <SecurityRow icon={<KeyRound size={18} />} title="Never exposed"
            desc="Tokens and secrets are never returned to the UI, never logged, never included in support exports." />
          <SecurityRow icon={<RefreshCw size={18} />} title="Rotation-friendly"
            desc="You can revoke Zaroori Jini from Amazon at any time from Seller Central → Apps & Services → Manage Your Apps. Reconnecting takes 60 seconds." />
          <p style={{ color: MUTED, fontSize: 14.5, lineHeight: 1.75, marginTop: 14 }}>
            Full trust posture:{" "}
            <Link to="/security" style={{ color: RED, fontWeight: 600 }}>zaroorijini.in/security</Link> ·{" "}
            <Link to="/privacy" style={{ color: RED, fontWeight: 600 }}>Privacy policy</Link>.
          </p>

          {/* ---- Disconnect / reconnect ---- */}
          <SectionTitle eyebrow="If you need to stop" title="How to disconnect & reconnect"
                         center={false} />
          <p style={{ color: MUTED, fontSize: 14.5, lineHeight: 1.75 }}>
            <b>To disconnect:</b> Open the Amazon connector in Zaroori Jini →{" "}
            <b>Disconnect</b>. We immediately delete our copy of your refresh
            token; Amazon retains your authorization until you also revoke it
            in Seller Central → <i>Apps &amp; Services → Manage Your Apps</i>.
          </p>
          <p style={{ color: MUTED, fontSize: 14.5, lineHeight: 1.75 }}>
            <b>To reconnect:</b> Repeat the 5-step flow above. Historical data
            previously imported stays intact — we simply resume live sync from
            the moment you reconnect.
          </p>

          {/* ---- Troubleshooting ---- */}
          <SectionTitle eyebrow="If something goes wrong" title="Common errors & fixes"
                         center={false} />
          <TroubleRow err="Amazon says 'The requested application could not be authorized'"
            fix="You may not be the Primary user on Amazon Seller Central, or your account is set to 'Manage Your Applications: restricted'. Ask the Primary user to complete the flow, or grant your account 'Manage Your Applications' access in Seller Central → Settings → User Permissions." />
          <TroubleRow err="redirect_uri_mismatch (technical error from Amazon)"
            fix="This means Amazon didn't accept our callback URL. Contact Zaroori Jini support — this is a config we own and can fix immediately." />
          <TroubleRow err="Connected, but Status shows 'waiting for AWS IAM'"
            fix="This is not an error. It means Zaroori Jini is running your Amazon connection in 5-minute polling mode. Real-time SQS events will turn on automatically once our AWS infra keys are attached — no action required from you." />
          <TroubleRow err="Historical import stuck at 'queued'"
            fix="Historical imports for Amazon require the AWS IAM layer. Your job is safely queued and will start as soon as IAM is enabled — no data loss, no re-authorization needed." />
          <TroubleRow err="I revoked Zaroori Jini on Amazon by mistake"
            fix="Just click 'Connect with Amazon' again in Zaroori Jini. The 5-step flow issues a fresh refresh token and picks up where you left off." />

          {/* ---- FAQ ---- */}
          <SectionTitle eyebrow="FAQ" title="Frequently asked questions"
                         center={false} />
          <FAQ q="Does connecting to Zaroori Jini cost anything on Amazon's side?"
                testid="help-faq-cost">
            No. Amazon does not charge sellers for authorizing an app. Zaroori Jini&apos;s
            pricing is on your subscription plan only.
          </FAQ>
          <FAQ q="How long does the refresh token last?" testid="help-faq-token">
            Amazon refresh tokens are long-lived — typically years — unless you
            revoke them. We refresh access tokens (1-hour tokens) automatically
            in the background.
          </FAQ>
          <FAQ q="Can I connect multiple Amazon accounts?" testid="help-faq-multi">
            Yes. Each Amazon account per region is a separate connection. You
            can add as many as your subscription allows.
          </FAQ>
          <FAQ q="What happens to my data if I cancel my Zaroori Jini plan?"
                testid="help-faq-cancel">
            Your data stays available for the retention window in your plan
            (see <Link to="/privacy" style={{ color: RED }}>Privacy Policy</Link>).
            You can export orders, inventory, and reports at any time. Refresh
            tokens are deleted immediately on account closure.
          </FAQ>
          <FAQ q="Do I need to give Zaroori Jini my Amazon password?"
                testid="help-faq-password">
            No — never. OAuth is designed so we never see your password. Amazon
            only tells us &quot;this user approved these permissions&quot; and hands
            us a token.
          </FAQ>
          <FAQ q="What if I sell on Amazon.in and Amazon.com both?"
                testid="help-faq-multiregion">
            Run the flow twice — once per region. Each connection is scoped to
            its region&apos;s marketplace so orders and inventory don&apos;t mix.
          </FAQ>
          <FAQ q="Where can I see everything Zaroori Jini pulled from Amazon?"
                testid="help-faq-visibility">
            Marketplace Hub → Amazon → <b>Data explorer</b> shows every entity
            we&apos;ve synced, with timestamps and the ability to re-fetch any
            single record.
          </FAQ>

          {/* ---- Support ---- */}
          <div style={{
            marginTop: 40, padding: 22, background: CREAM, border: `1px solid ${BORDER}`,
            borderRadius: 6,
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: INK }}>Still need help?</div>
            <p style={{ color: MUTED, fontSize: 14, marginTop: 6, marginBottom: 12 }}>
              Our support team responds within 4 business hours (Enterprise plan:
              1 hour, 24×7).
            </p>
            <a href="mailto:jini@zarooriinfotech.in" style={{
              display: "inline-block", padding: "10px 18px", background: RED,
              color: "#fff", borderRadius: 4, fontWeight: 600, fontSize: 13,
              textDecoration: "none",
            }}>Contact Zaroori Jini support</a>
          </div>

        </div>
      </Section>
    </MarketingShell>
  );
}

/* ------------------------- Small subcomponents ------------------------- */

const PermRow = ({ scope, desc }) => (
  <div style={{
    display: "grid", gridTemplateColumns: "160px 1fr", gap: 20,
    padding: "12px 0", borderBottom: `1px solid ${BORDER}`,
  }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{scope}</div>
    <div style={{ fontSize: 13.5, color: MUTED, lineHeight: 1.65 }}>{desc}</div>
  </div>
);

const SecurityRow = ({ icon, title, desc }) => (
  <div style={{
    display: "grid", gridTemplateColumns: "36px 1fr", gap: 14,
    padding: "10px 0", alignItems: "flex-start",
  }}>
    <div style={{ color: RED, marginTop: 2 }}>{icon}</div>
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, color: INK }}>{title}</div>
      <div style={{ fontSize: 13.5, color: MUTED, marginTop: 3, lineHeight: 1.6 }}>{desc}</div>
    </div>
  </div>
);

const TroubleRow = ({ err, fix }) => (
  <div style={{
    background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 6,
    padding: "14px 16px", marginBottom: 10,
  }}>
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <AlertTriangle size={16} style={{ color: "#B45309", marginTop: 2, flexShrink: 0 }} />
      <div style={{ fontSize: 13.5, fontWeight: 600, color: INK }}>{err}</div>
    </div>
    <div style={{ fontSize: 13, color: MUTED, marginTop: 6, marginLeft: 26, lineHeight: 1.65 }}>{fix}</div>
  </div>
);
